import Markdown from 'react-markdown';

import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';

import type { FieldTemplatePropsTrace } from '@/components/forms/components/formTemplates/field';
import { LinkIcon } from '@/components/forms/components/formTemplates/field/linkIcon.field';
import { getFieldPath } from '@/components/forms/utils/links';

type FormDescriptionProps = Partial<
  Pick<FieldTemplatePropsTrace, 'id' | 'rawDescription' | 'classNames'>
>;

export const FormDescription = ({
  id,
  rawDescription,
  classNames
}: FormDescriptionProps) => {
  if (!rawDescription) return null;
  if (!id) console.error('Form I9OLF: id is undefined');

  return (
    <div
      className={cn('pb-2 text-muted-foreground text-xs', classNames)}
      data-cy={`description-${id}`}
    >
      <Markdown>{rawDescription}</Markdown>
    </div>
  );
};

type FormLabelProps = Partial<
  Pick<
    FieldTemplatePropsTrace,
    'label' | 'id' | 'required' | 'formContext' | 'classNames'
  >
>;

export const FormLabel = ({
  label,
  id,
  required,
  formContext,
  classNames
}: FormLabelProps) => {
  if (!label) return null;
  const { isMainForm, lockedPaths = [], id: formId } = formContext?.links || {};

  const isLargeLabel = !(
    isMainForm || lockedPaths.includes(getFieldPath(formId, id!))
  );
  return (
    <div
      className={cn(
        'relative inline-flex w-full max-w-[90%] flex-row flex-nowrap items-center bg-inherit pb-2',
        classNames
      )}
    >
      <Label
        className={cn(
          'font-semibold text-base text-foreground',
          required && 'after:ml-1 after:text-destructive after:content-["*"]',
          isLargeLabel && 'grow'
        )}
        data-cy={`label-${id}`}
        data-input-is-required={required}
        data-is-large={isLargeLabel}
        htmlFor={id}
      >
        {label}
      </Label>
      {id && <LinkIcon formContext={formContext} id={id} />}
    </div>
  );
};

type FormHelpProps = Partial<
  Pick<FieldTemplatePropsTrace, 'id' | 'rawHelp' | 'classNames'>
>;

export const FormHelp = ({ id, rawHelp, classNames }: FormHelpProps) => {
  if (!rawHelp) return null;
  if (!id) console.error('Form H9OLF: id is undefined');

  return (
    <div
      className={cn('mt-2 text-muted-foreground text-xs', classNames)}
      data-cy={`helper-text-${id}`}
    >
      <Markdown>{rawHelp}</Markdown>
    </div>
  );
};
